import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, User, ShieldCheck, ArrowUpRight, Calendar, FileText, Users, Clock, CheckCircle2, GraduationCap, Sparkles, X, CreditCard, Globe, Coins, NotebookPen } from "lucide-react";
import { Button } from "@/components/ui/button";
import SubscribeDialog from "@/components/SubscribeDialog";
import Testimonials from "@/components/Testimonials";
import { PublicIncidentBanner } from "@/components/IncidentBanner";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";
import { useCurrency } from "@/context/CurrencyContext";
import { Money } from "@/components/Money";
import { PromoBanner, PromoCountdown } from "@/components/PromoBanner";
import { MetiersSection } from "@/components/landing/MetiersSection";
import { api } from "@/api";

const roles = [
  {
    key: "chef",
    title: "Chef d'entreprise",
    subtitle: "Pilotez votre équipe",
    code: "PAG-ENT-xxxx",
    icon: Building2,
    description: "Gérez salariés, plannings, congés, paies et documents RH depuis un seul tableau de bord.",
  },
  {
    key: "salarie",
    title: "Salarié",
    subtitle: "Mon espace personnel",
    code: "PAG-SAL-xxxx",
    icon: User,
    description: "Consultez votre planning, vos congés, vos bulletins de paie et vos messages.",
  },
  {
    key: "admin",
    title: "Admin PAG",
    subtitle: "Administration globale",
    code: "PAG-ADMIN-xxx",
    icon: ShieldCheck,
    description: "Gestion des entreprises, paiements, blocages automatiques et support.",
  },
];

const COMPARISON_ROWS = [
  {
    task: "Planning hebdomadaire des salariés",
    without: "Tableur Excel partagé, erreurs fréquentes",
    with: "Vues semaine/mois, couleurs par type, 1 clic",
  },
  {
    task: "Demande de congé d'un salarié",
    without: "Email ou papier, perdu dans la boîte",
    with: "Demande dans l'app, validation chef 1 clic",
  },
  {
    task: "Calcul du salaire brut mensuel",
    without: "Ressaisie manuelle, risque d'erreur",
    with: "Heures × taux calculé automatiquement",
  },
  {
    task: "Envoi des bulletins de paie",
    without: "Impression + enveloppe ou email individuel",
    with: "Dépôt PDF, téléchargement sécurisé salarié",
  },
  {
    task: "Partage des documents RH (contrats, attestations)",
    without: "Pochette papier ou email, version perdue",
    with: "Coffre-fort numérique cloisonné par salarié",
  },
  {
    task: "Suivi du solde congés payés",
    without: "Fichier Excel à jour… quand on y pense",
    with: "Solde temps réel, visible salarié + chef",
  },
  {
    task: "Notifications à l'équipe",
    without: "Groupe WhatsApp ou email de masse",
    with: "Messages ciblés ou broadcast intégrés",
  },
  {
    task: "Formation des nouveaux chefs",
    without: "Débrouille + tutos YouTube",
    with: "Formation Parc Avenue incluse, sessions hebdo",
  },
  {
    task: "Sécurité et accès",
    without: "Mots de passe partagés, comptes mélangés",
    with: "Codes personnels, 3 rôles cloisonnés",
  },
];

export default function Landing() {
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  // v2.0.01 : 4 nouvelles offres (TOP / MEDIUM / STANDARD / MAXIMUM BUDGET) sur devis.
  // Fallback v1 conservé pour rétro-compat en cas d'absence des plans v2.
  const displayPlans = React.useMemo(() => {
    const v2 = plans.filter((p) => p.version === "v2");
    return v2.length >= 2 ? v2 : plans.filter((p) => p.version !== "v2");
  }, [plans]);
  const isV2 = React.useMemo(
    () => displayPlans.length > 0 && displayPlans.every((p) => p.version === "v2"),
    [displayPlans]
  );
  // v2.0.21 — Les prix d'accroche étaient codés en dur (33 €/66 € de l'ancienne grille Solo).
  // Ils sont désormais dérivés du catalogue réel : plus jamais de tarif périmé sur la landing.
  const priceRange = React.useMemo(() => {
    const mins = displayPlans.map((p) => p.monthly_price_min ?? p.monthly_price).filter((v) => v != null);
    const maxs = displayPlans.map((p) => p.monthly_price_max ?? p.monthly_price).filter((v) => v != null);
    const openEnded = displayPlans.some((p) => (p.monthly_price_min ?? p.monthly_price) != null
      && (p.monthly_price_max ?? p.monthly_price) == null);
    return {
      min: mins.length ? Math.min(...mins) : null,
      max: maxs.length ? Math.max(...maxs) : null,
      count: displayPlans.length,
      openEnded,
    };
  }, [displayPlans]);
  const [announcements, setAnnouncements] = useState([]);
  const [billingCycle, setBillingCycle] = useState("annual"); // annual | monthly
  const [autoPromos, setAutoPromos] = useState({}); // v2.0.17 Phase 37 — pastilles promo
  const [activeOffers, setActiveOffers] = useState([]); // v2.0.21 — bandeau offres en cours

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    // ?version=v2 : ne récupère que les 4 tiers v2.0.01 côté landing (évite d'expédier
    // le catalogue legacy en clair). Fallback en cas d'échec: catalogue complet.
    api.get("/public/plans?version=v2")
      .then((r) => setPlans(r.data && r.data.length ? r.data : []))
      .catch(() => api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {}));
    api.get("/public/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    // v2.0.17 (Phase 37) — Charge les promos auto-apply visibles
    api.get("/public/promotions/auto").then((r) => setAutoPromos(r.data || {})).catch(() => {});
    // v2.0.21 — Offres publiques en cours (bandeau + fenêtre de validité)
    api.get("/public/promotions/active").then((r) => setActiveOffers(r.data?.items || [])).catch(() => {});
  }, []);

  // v2.0.05 — Stripe côté client retiré. Les CTA pointent tous vers /inscription → PayPal.

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <PublicIncidentBanner />
      {/* Top marquee */}
      <div className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white overflow-hidden">
        <div className="flex whitespace-nowrap py-2 ticker-track text-xs tracking-widest uppercase font-semibold">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-8 px-8">
              <span>Espace Entreprise PAG</span>
              <span className="text-[#FFD700]">●</span>
              <span>Plateforme sécurisée</span>
              <span className="text-[#FFD700]">●</span>
              <span>Gestion RH unifiée</span>
              <span className="text-[#FFD700]">●</span>
              <span>Plannings · Congés · Paies · Documents</span>
              <span className="text-[#FFD700]">●</span>
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG Business Manager" className="h-10 w-10 object-contain" data-testid="brand-logo" />
            <div className="flex flex-col">
              <span className="font-display font-black text-xl tracking-tight leading-none">PAG</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold mt-0.5">
                Business Manager
              </span>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <LanguageCurrencySwitcher />
            <Link to="/simulateur-salaire" className="hidden lg:inline-flex text-sm font-semibold text-[#0A0A0A] hover:text-[#002FA7] transition-colors px-3" data-testid="nav-simulateur">Simulateur</Link>
            <Link to="/formations" className="hidden md:inline-flex text-sm font-semibold text-[#0A0A0A] hover:text-[#002FA7] transition-colors px-3" data-testid="nav-formations">Formations</Link>
            <Link to="/carrieres" className="hidden md:inline-flex text-sm font-semibold text-[#0A0A0A] hover:text-[#002FA7] transition-colors px-3" data-testid="nav-carrieres">Carrières</Link>
            <a href="#metiers" className="hidden lg:inline-flex text-sm font-semibold text-[#0A0A0A] hover:text-[#002FA7] transition-colors px-3" data-testid="nav-metiers">Métiers</a>
            <a href="#tarif" className="hidden md:inline-flex text-sm font-semibold text-[#002FA7] hover:text-[#002175] transition-colors px-3" data-testid="nav-tarif">Tarifs</a>
            <Link to="/inscription" className="hidden md:inline-flex">
              <Button data-testid="header-signup-btn" className="text-sm font-semibold bg-[#002FA7] hover:bg-[#002175] text-white rounded-md">
                S'inscrire
              </Button>
            </Link>
            <Link to="/login/chef">
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                className="text-sm font-semibold rounded-md hover:bg-[#F8F9FA]"
              >
                Connexion
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Announcements banner */}
      {announcements.length > 0 && (
        <section className="border-b border-[#DEE2E6] bg-[#FFD700]" data-testid="announcements-banner">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-start gap-4 flex-wrap">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#0A0A0A] flex items-center gap-2 shrink-0">
              <Sparkles className="h-4 w-4" /> // Mises à jour
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-[#0A0A0A] flex-1">
              {announcements.slice(0, 3).map((a) => (
                <div key={a.id} className="flex items-start gap-2" data-testid={`announcement-${a.id}`}>
                  <span className="font-bold">{a.title}</span>
                  <span className="opacity-80">— {a.content.slice(0, 110)}{a.content.length > 110 ? "…" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HERO */}
      <section className="relative border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <img src="/pag-logo.png" alt="PAG Business Manager" className="h-20 w-20 lg:h-24 lg:w-24 object-contain" data-testid="hero-logo" />
              <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold" data-testid="hero-eyebrow">
                · PAG Business Manager<br />· Édition 2026
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95]" data-testid="hero-title">
              Espace<br />
              Entreprise<br />
              <span className="text-[#002FA7]">PAG.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#495057] max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              Gérez votre entreprise, vos salariés, vos plannings, les congés, les paies et les documents RH depuis une seule plateforme sécurisée.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/login/chef">
                <Button
                  data-testid="cta-chef-btn"
                  className="h-12 px-6 bg-[#002FA7] hover:bg-[#002175] text-white rounded-md font-semibold text-sm"
                >
                  Accès Chef d'entreprise <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login/salarie">
                <Button
                  data-testid="cta-salarie-btn"
                  variant="outline"
                  className="h-12 px-6 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white rounded-md font-semibold text-sm"
                >
                  Accès Salarié
                </Button>
              </Link>
              <Link to="/login/admin">
                <Button
                  data-testid="cta-admin-btn"
                  variant="ghost"
                  className="h-12 px-6 hover:bg-[#F8F9FA] rounded-md font-semibold text-sm"
                >
                  Admin PAG →
                </Button>
              </Link>
            </div>
          </div>

          {/* Side stats card */}
          <div className="lg:col-span-5 flex flex-col gap-0 border border-[#0A0A0A]">
            <div className="p-6 border-b border-[#0A0A0A] flex items-center justify-between bg-[#0A0A0A] text-white">
              <span className="text-xs uppercase tracking-[0.25em] font-bold">// Système actif</span>
              <span className="h-2 w-2 bg-[#00E676] rounded-full animate-pulse" />
            </div>
            <div className="grid grid-cols-2 divide-x divide-[#0A0A0A]">
              <Stat value="3" label="Portails" />
              <Stat value="RH" label="Tout-en-un" />
            </div>
            <div className="grid grid-cols-2 divide-x divide-[#0A0A0A] border-t border-[#0A0A0A]">
              <Stat value="48h" label="Paiement" />
              <Stat value="100%" label="Sécurisé" />
            </div>
            <div className="p-6 border-t border-[#0A0A0A] bg-[#F8F9FA]">
              <div className="text-xs uppercase tracking-[0.25em] text-[#868E96] font-semibold mb-2">Codes d'accès</div>
              <div className="font-mono text-sm space-y-1">
                <div><span className="text-[#868E96]">ENT/</span> PAG-ENT-8492</div>
                <div><span className="text-[#868E96]">SAL/</span> PAG-SAL-2391</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Phase 84 — Bloc-note : prochaines mises à jour */}
      {announcements.length > 0 && (
        <section className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white" data-testid="landing-upcoming">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-16 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3 flex items-center gap-2"><NotebookPen className="h-4 w-4" /> // Bloc-note PAG</div>
              <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-[1.05]">Prochainement sur votre plateforme.</h2>
              <p className="text-sm text-white/70 mt-4">Nouveaux modules, améliorations et maintenances planifiées : nous vous tenons informés avant chaque déploiement.</p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {announcements.slice(0, 4).map((a) => (
                <article key={a.id} className="border border-white/15 p-5 flex flex-col bg-white/[0.03]" data-testid={`upcoming-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${a.kind === "maintenance" ? "bg-[#E60000]" : a.kind === "new_module" ? "bg-[#059669]" : a.kind === "info" ? "bg-white text-[#0A0A0A]" : "bg-[#002FA7]"}`}>{{ update: "Mise à jour", new_module: "Nouveau module", info: "Information", maintenance: "Maintenance" }[a.kind] || "Mise à jour"}</span>
                    {a.period && <span className="text-[10px] font-bold text-[#FFD700]">{a.period}</span>}
                  </div>
                  <div className="font-display font-bold text-lg leading-tight">{a.title}</div>
                  <p className="text-sm text-white/70 mt-2 whitespace-pre-wrap line-clamp-4">{a.content}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3 role portals */}
      <section className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-3">Trois portails</div>
              <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight">
                Un espace dédié pour chaque rôle.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-0 border border-[#0A0A0A]">
            {roles.map((r, idx) => {
              const Icon = r.icon;
              return (
                <Link
                  key={r.key}
                  to={`/login/${r.key}`}
                  data-testid={`role-card-${r.key}`}
                  className={`group p-8 flex flex-col min-h-[320px] transition-colors hover:bg-[#0A0A0A] hover:text-white ${
                    idx < 2 ? "border-r border-[#0A0A0A]" : ""
                  } ${idx < 2 ? "md:border-b-0 border-b border-[#0A0A0A]" : ""}`}
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="h-12 w-12 border border-current flex items-center justify-center">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-xs uppercase tracking-[0.25em] font-semibold opacity-60">0{idx + 1}</span>
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight mb-1">{r.title}</h3>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-60 mb-4">{r.subtitle}</div>
                  <p className="text-sm leading-relaxed opacity-80 mb-6">{r.description}</p>
                  <div className="mt-auto pt-4 border-t border-current/20 flex items-center justify-between">
                    <span className="font-mono text-xs opacity-70">{r.code}</span>
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-[#DEE2E6] bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-8">
            <Feature icon={Users} title="Gestion des salariés" desc="Ajoutez, modifiez et suivez vos équipes. Chaque salarié reçoit son code d'accès personnel." />
            <Feature icon={Calendar} title="Plannings intelligents" desc="Vues semaine et mois. Travail, repos, congés, maladie, jours fériés : tout en un clic." />
            <Feature icon={FileText} title="Bulletins & documents RH" desc="Dépôt sécurisé des bulletins de paie PDF et de tous les documents administratifs." />
            <Feature icon={Clock} title="Congés & absences" desc="Demandes, validations, solde de congés payés et signalements d'absences centralisés." />
            <Feature icon={CheckCircle2} title="Statut paiement" desc="Blocage automatique après 48h sans paiement. Déblocage uniquement par Admin PAG." />
            <Feature icon={ShieldCheck} title="Accès cloisonnés" desc="Admin, Chef, Salarié : chacun voit exactement ce dont il a besoin, rien de plus." />
            <FeatureI18n />
            <FeatureCurrency />
          </div>
        </div>
      </section>

      {/* Phase 89 — Modules métier */}
      <MetiersSection />

      {/* Tarif (4 plans) + Formations */}
      <section id="tarif" className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-4">// Abonnements</div>
              <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Quatre formules,<br />
                un seul outil.
              </h2>
              <p className="mt-6 text-base text-white/70 max-w-xl">
                Du freelance à la franchise, choisissez le plan adapté à votre structure. Validation admin sous 24h après inscription.
              </p>
            </div>
            <div className="lg:col-span-5 flex items-end justify-end">
              <Link to="/inscription" data-testid="cta-inscription-header" className="inline-flex items-center h-12 px-6 border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-[#0A0A0A] transition-colors font-semibold text-sm">
                Souscrire maintenant · Paiement direct <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* v2.0.21 — Bandeau des offres en cours, avec dates de validité + décompte */}
          <PromoBanner offers={activeOffers} onCta={() => navigate("/inscription")} />

          {/* Toggle Mensuel/Annuel — visible en v1 et v2 */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex border border-white/30 bg-white/[0.03]" data-testid="billing-cycle-toggle">
              <button
                type="button"
                onClick={() => setBillingCycle("annual")}
                data-testid="cycle-annual"
                className={`px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${billingCycle === "annual" ? "bg-[#FFD700] text-[#0A0A0A]" : "text-white/70 hover:text-white"}`}
              >
                Annuel <span className="ml-2 text-[10px] normal-case tracking-normal opacity-75">avec engagement de 1 an</span>
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                data-testid="cycle-monthly"
                className={`px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${billingCycle === "monthly" ? "bg-[#FFD700] text-[#0A0A0A]" : "text-white/70 hover:text-white"}`}
              >
                Mensuel <span className="ml-2 text-[10px] normal-case tracking-normal opacity-75">Sans engagement</span>
              </button>
            </div>
          </div>

          {/* Plans grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white" data-testid="plans-grid">
            {displayPlans.length === 0 ? (
              <div className="col-span-full p-10 text-center text-white/60 text-sm">Chargement des formules…</div>
            ) : displayPlans.map((p, idx) => {
              const isOnDevis = p.on_devis === true;
              const price = billingCycle === "annual" ? p.annual_price : p.monthly_price;
              const priceMin = billingCycle === "annual" ? p.annual_price_min : p.monthly_price_min;
              const priceMax = billingCycle === "annual" ? p.annual_price_max : p.monthly_price_max;
              // v2.0.17 (Phase 37) — Récupère la promo auto pour ce plan+cycle
              const promo = autoPromos[`${p.key}_${billingCycle}`];
              return (
              <div
                key={p.key}
                data-testid={`plan-card-${p.key}`}
                className={`p-7 lg:p-8 flex flex-col relative ${idx < displayPlans.length - 1 ? "lg:border-r border-white/20" : ""} ${p.popular ? "bg-[#002FA7]/20" : "bg-white/[0.03]"}`}
              >
                {promo && (
                  <div className="absolute -top-3 -right-3 bg-[#F59E0B] text-black font-black text-sm px-3 py-1 shadow-lg z-10 rotate-3" data-testid={`plan-promo-badge-${p.key}`}>
                    {promo.discount_type === "percent" ? `-${promo.discount_value}%` : `-${promo.discount_value}€`}
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="text-[10px] uppercase tracking-[0.25em] font-bold"
                    style={{
                      // Fix contraste : les couleurs de plan sombres sont invisibles sur le fond noir #tarif.
                      // Luminance < 0.35 → utiliser jaune PAG à la place.
                      color: (() => {
                        const c = (p.color || "#FFFFFF").replace("#", "");
                        if (c.length !== 6) return p.color;
                        const r = parseInt(c.slice(0, 2), 16);
                        const g = parseInt(c.slice(2, 4), 16);
                        const b = parseInt(c.slice(4, 6), 16);
                        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                        return lum < 0.35 ? "#FFD700" : p.color;
                      })(),
                    }}
                  >
                    {p.name.replace("PAG ", "")}
                  </div>
                  {p.popular && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#FFD700] text-[#0A0A0A] px-2 py-0.5">Populaire</span>}
                </div>
                <div className="text-xs text-white/60 mb-4">{p.tagline}</div>
                <div className="font-display text-5xl font-black tracking-tighter leading-none" data-testid={`plan-price-${p.key}`}>
                  {isOnDevis && priceMin != null ? (
                    // v2.0.21 — Une promo auto s'applique aussi aux formules sur devis :
                    // la fourchette barrée + la fourchette remisée (avant : badge -20% mais
                    // prix inchangé, donc incompréhensible pour le visiteur).
                    (() => {
                      const disc = (v) => {
                        if (!promo || v == null) return null;
                        const d = promo.discount_type === "percent"
                          ? v * promo.discount_value / 100
                          : Math.min(v, promo.discount_value);
                        return Math.max(0, Math.round((v - d) * 100) / 100);
                      };
                      const dMin = disc(priceMin);
                      const dMax = disc(priceMax);
                      const range = (a, b) => b != null
                        ? <><Money amount={a} showOriginal={false} /><span className="text-2xl">–<Money amount={b} showOriginal={false} /></span></>
                        : <><Money amount={a} showOriginal={false} />+</>;
                      if (dMin == null) return range(priceMin, priceMax);
                      return (
                        <>
                          <span className="block text-xl text-white/40 line-through leading-none mb-1"
                                data-testid={`plan-price-strike-${p.key}`}>
                            {range(priceMin, priceMax)}
                          </span>
                          <span className="text-[#FFD700]">{range(dMin, dMax)}</span>
                        </>
                      );
                    })()
                  ) : (
                    promo && price != null ? (
                      <>
                        <span className="text-2xl text-white/40 line-through mr-2" data-testid={`plan-price-strike-${p.key}`}><Money amount={promo.base_price} showOriginal={false} /></span>
                        <span className="text-[#FFD700]"><Money amount={promo.final_price} showOriginal={false} /></span>
                      </>
                    ) : (
                      price != null ? <Money amount={price} showOriginal={false} /> : "—"
                    )
                  )}
                </div>
                {promo && (
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F59E0B] mt-1" data-testid={`plan-promo-label-${p.key}`}>
                    {promo.promotion_name} · Économie {isOnDevis ? `-${promo.discount_type === "percent" ? `${promo.discount_value}%` : `${promo.discount_value}€`}` : `-${promo.discount_amount}€`}
                  </div>
                )}
                {promo && (
                  <PromoCountdown
                    endsInSeconds={promo.ends_in_seconds}
                    endsAt={promo.ends_at}
                    className="text-[10px] text-[#FFD700] mt-1"
                    testid={`plan-promo-countdown-${p.key}`}
                  />
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/60 mt-1">
                  {billingCycle === "annual"
                    ? <>/ an{isOnDevis ? <span className="text-white/40"> — sur devis</span> : null}</>
                    : <>/ mois{isOnDevis ? <span className="text-white/40"> — sur devis</span> : null}</>}
                </div>
                <ul className="mt-5 space-y-1.5 text-xs text-white/85 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/inscription?plan=${p.key}&cycle=${billingCycle}`}
                  data-testid={`plan-cta-${p.key}`}
                  className={`mt-6 inline-flex items-center justify-center h-11 px-4 font-semibold text-xs uppercase tracking-wider transition-colors ${p.popular ? "bg-[#FFD700] text-[#0A0A0A] hover:bg-white" : "border border-white hover:bg-white hover:text-[#0A0A0A]"}`}
                >
                  Souscrire · Paiement direct <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </div>
              );
            })}
          </div>

          {/* Paiement info + formation */}
          <div className="grid lg:grid-cols-12 gap-8 mt-12">
            <div className="lg:col-span-7 border border-white/30 p-6 lg:p-8 bg-white/[0.02]">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] font-semibold text-white/60 mb-2">Paiement & activation</div>
                  <p className="text-sm text-white/80 max-w-md">Paiement immédiat via <b className="text-[#FFD700]">PayPal</b> (carte bancaire acceptée en invité). Votre espace entreprise est activé automatiquement dès la confirmation.</p>
                </div>
                <div className="flex flex-wrap gap-3 ml-auto">
                  <Link to="/inscription" data-testid="tarif-subscribe" className="inline-flex items-center h-11 px-5 bg-[#FFD700] text-[#0A0A0A] hover:bg-white transition-colors font-semibold text-sm">
                    S'inscrire · Payer PayPal <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                  <a href="tel:+33970701792" className="inline-flex items-center h-11 px-5 border border-white/30 hover:bg-white hover:text-[#0A0A0A] transition-colors font-semibold text-sm font-mono">
                    09 70 70 17 92
                  </a>
                </div>
              </div>
            </div>

            {/* Formations */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">// Formation incluse</div>
              <div className="border border-white p-6 flex-1 flex flex-col">
                <h3 className="font-display text-2xl font-black tracking-tight mb-3">Chaque chef formé.</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-4">
                  Le groupe <span className="text-white font-semibold">Parc Avenue</span> propose une formation complète à chaque nouveau chef d'entreprise abonné.
                </p>
                <Link to="/formations" data-testid="formations-calendar-link" className="mt-auto inline-flex items-center justify-between p-3 bg-[#FFD700] text-[#0A0A0A] hover:bg-white transition-colors font-semibold text-sm">
                  <span>Voir le calendrier <GraduationCap className="inline h-4 w-4 ml-1" /></span>
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <Testimonials />

      {/* Comparatif Avec / Sans PAG */}
      <section className="border-b border-[#DEE2E6] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-4">// Comparatif</div>
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-[1] mb-10">
            Avec PAG, vous gagnez<br />
            du temps. Et de l'argent.
          </h2>

          <div className="border border-[#0A0A0A]" data-testid="comparison-table">
            {/* -- Desktop table view (md+) -- */}
            <div className="hidden md:block">
              {/* Table head */}
              <div className="grid grid-cols-12 bg-[#0A0A0A] text-white">
                <div className="col-span-6 p-4 lg:p-5 text-xs uppercase tracking-[0.25em] font-bold">Tâche quotidienne RH</div>
                <div className="col-span-3 p-4 lg:p-5 text-xs uppercase tracking-[0.25em] font-bold border-l border-white/20 flex items-center gap-2">
                  <X className="h-4 w-4 text-[#E60000]" /> Sans PAG
                </div>
                <div className="col-span-3 p-4 lg:p-5 text-xs uppercase tracking-[0.25em] font-bold border-l border-white/20 bg-[#002FA7] flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#FFD700]" /> Avec PAG
                </div>
              </div>

              {/* Rows */}
              {COMPARISON_ROWS.map((row, i) => (
                <div key={i} className={`grid grid-cols-12 text-sm ${i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"} ${i < COMPARISON_ROWS.length - 1 ? "border-b border-[#DEE2E6]" : ""}`}>
                  <div className="col-span-6 p-4 lg:p-5 font-semibold">{row.task}</div>
                  <div className="col-span-3 p-4 lg:p-5 border-l border-[#DEE2E6] text-[#495057] flex items-start gap-2">
                    <X className="h-4 w-4 text-[#E60000] flex-shrink-0 mt-0.5" />
                    <span>{row.without}</span>
                  </div>
                  <div className="col-span-3 p-4 lg:p-5 border-l border-[#DEE2E6] bg-[#002FA7]/5 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#059669] flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">{row.with}</span>
                  </div>
                </div>
              ))}

              {/* Total row */}
              <div className="grid grid-cols-12 bg-[#0A0A0A] text-white border-t-2 border-[#FFD700]">
                <div className="col-span-6 p-5 lg:p-6 font-display text-lg lg:text-xl font-black tracking-tight">Bilan mensuel estimé</div>
                <div className="col-span-3 p-5 lg:p-6 border-l border-white/20">
                  <div className="text-xs uppercase tracking-[0.2em] opacity-60 font-semibold">Temps perdu</div>
                  <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1">~15h / mois</div>
                </div>
                <div className="col-span-3 p-5 lg:p-6 border-l border-white/20 bg-[#002FA7]">
                  <div className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold text-[#FFD700]">Temps récupéré</div>
                  <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1">~14h / mois</div>
                </div>
              </div>
            </div>

            {/* -- Mobile stacked view (< md) -- */}
            <div className="md:hidden" data-testid="comparison-mobile">
              <div className="bg-[#0A0A0A] text-white p-4 text-[10px] uppercase tracking-[0.25em] font-bold">
                Tâches quotidiennes RH
              </div>
              {COMPARISON_ROWS.map((row, i) => (
                <div key={i} className={`p-4 ${i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"} ${i < COMPARISON_ROWS.length - 1 ? "border-b border-[#DEE2E6]" : ""}`}>
                  <div className="font-semibold text-sm mb-3">{row.task}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-[#DEE2E6] p-3 bg-white">
                      <div className="flex items-center gap-1.5 mb-2">
                        <X className="h-3.5 w-3.5 text-[#E60000] flex-shrink-0" />
                        <span className="text-[9px] uppercase tracking-wider font-bold text-[#495057]">Sans PAG</span>
                      </div>
                      <div className="text-xs text-[#495057] leading-snug">{row.without}</div>
                    </div>
                    <div className="border border-[#002FA7] p-3 bg-[#002FA7]/5">
                      <div className="flex items-center gap-1.5 mb-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#059669] flex-shrink-0" />
                        <span className="text-[9px] uppercase tracking-wider font-bold text-[#002FA7]">Avec PAG</span>
                      </div>
                      <div className="text-xs font-semibold leading-snug">{row.with}</div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Total row mobile */}
              <div className="bg-[#0A0A0A] text-white p-4 border-t-2 border-[#FFD700]">
                <div className="font-display text-lg font-black tracking-tight mb-3">Bilan mensuel estimé</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-white/20 p-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] opacity-60 font-semibold mb-1">Temps perdu</div>
                    <div className="font-display text-xl font-black tracking-tighter">~15h/mois</div>
                  </div>
                  <div className="border border-[#FFD700] bg-[#002FA7] p-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#FFD700] font-semibold mb-1">Temps récupéré</div>
                    <div className="font-display text-xl font-black tracking-tighter">~14h/mois</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/inscription" data-testid="comparison-cta" className="inline-flex items-center h-12 px-6 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold text-sm transition-colors">
              Choisir mon abonnement <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
            {priceRange.min != null && (
              <div className="text-sm text-[#495057]" data-testid="comparison-price-note">
                À partir de <span className="font-bold text-[#0A0A0A]"><Money amount={priceRange.min} showOriginal={false} />/mois</span>
                {priceRange.count > 1 && <> — {priceRange.count} formules sur devis</>}
                {priceRange.max != null && <>, jusqu'à <Money amount={priceRange.max} showOriginal={false} />/mois</>}
                {priceRange.openEnded ? <> puis sur mesure au-delà.</> : <>.</>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="border-b border-[#DEE2E6] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-4">// Support</div>
            <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-[1]">
              Une question ?<br />
              Notre équipe<br />
              vous répond.
            </h2>
            <p className="mt-6 text-base text-[#495057] max-w-lg leading-relaxed">
              Assistance technique, administration des comptes entreprise, paiements : contactez-nous directement.
            </p>
          </div>
          <div className="border border-[#0A0A0A]">
            <a
              href="mailto:contact@parcavenue.eu"
              data-testid="support-email"
              className="group flex items-center justify-between p-6 lg:p-8 border-b border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <div>
                <div className="text-xs uppercase tracking-[0.25em] font-semibold opacity-60 mb-2">Email</div>
                <div className="font-display text-xl lg:text-2xl font-bold tracking-tight">contact@parcavenue.eu</div>
              </div>
              <ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="tel:+33970701792"
              data-testid="support-phone"
              className="group flex items-center justify-between p-6 lg:p-8 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              <div>
                <div className="text-xs uppercase tracking-[0.25em] font-semibold opacity-60 mb-2">Téléphone</div>
                <div className="font-display text-xl lg:text-2xl font-bold tracking-tight font-mono">09 70 70 17 92</div>
              </div>
              <ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid md:grid-cols-3 gap-6 items-start">
          <div className="flex items-center gap-3">
            <span className="inline-block w-2.5 h-2.5 bg-[#002FA7]" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] opacity-60 border-l border-white/20 pl-3">
              © 2026 — Espace Entreprise
            </span>
          </div>
          <div className="text-sm space-y-1" data-testid="footer-support">
            <div className="text-xs uppercase tracking-[0.25em] opacity-60 font-semibold mb-2">Support</div>
            <a href="mailto:contact@parcavenue.eu" className="block hover:text-[#FFD700] transition-colors">contact@parcavenue.eu</a>
            <a href="tel:+33970701792" className="block hover:text-[#FFD700] transition-colors font-mono">09 70 70 17 92</a>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] opacity-60 md:text-right space-y-2">
            <div>Plateforme RH · Paiement · Sécurisé</div>
            <div className="flex flex-wrap md:justify-end gap-x-4 gap-y-1 normal-case tracking-normal text-sm opacity-100" data-testid="footer-legal">
              <Link to="/legal/mentions_legales" className="hover:text-[#FFD700] transition-colors" data-testid="footer-legal-mentions">Mentions légales</Link>
              <Link to="/legal/cgv" className="hover:text-[#FFD700] transition-colors" data-testid="footer-legal-cgv">CGV</Link>
              <Link to="/legal/cgu" className="hover:text-[#FFD700] transition-colors" data-testid="footer-legal-cgu">CGU</Link>
              <Link to="/legal/confidentialite" className="hover:text-[#FFD700] transition-colors" data-testid="footer-legal-confidentialite">Confidentialité</Link>
              <Link to="/status" className="hover:text-[#FFD700] transition-colors" data-testid="footer-status">Statut</Link>
              <Link to="/carrieres" className="hover:text-[#FFD700] transition-colors" data-testid="footer-carrieres">Carrières</Link>
            </div>
          </div>
        </div>
      </footer>

      <SubscribeDialog open={subscribeOpen} onOpenChange={setSubscribeOpen} />

      {/* Mobile sticky CTA — visible uniquement < md */}
      <div
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A] border-t-2 border-[#FFD700] shadow-[0_-4px_16px_rgba(0,0,0,0.25)]"
      >
        <Link
          to="/devis"
          data-testid="mobile-cta-link"
          className="flex items-center justify-between px-4 py-3.5 gap-3"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFD700] font-bold">Devis sur mesure</div>
            <div className="text-white font-display font-black text-base tracking-tight leading-tight" data-testid="mobile-cta-price">
              Modules à la carte{priceRange.min != null && <> dès <Money amount={priceRange.min} showOriginal={false} />/mois</>}
            </div>
          </div>
          <span className="inline-flex items-center justify-center h-11 px-4 bg-[#FFD700] text-[#0A0A0A] font-bold text-xs uppercase tracking-wider flex-shrink-0">
            Demander <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </span>
        </Link>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="p-6">
      <div className="font-display text-4xl lg:text-5xl font-black tracking-tighter">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.25em] text-[#868E96] font-semibold">{label}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="p-6 bg-white border border-[#DEE2E6] elev-hover">
      <Icon className="h-6 w-6 text-[#002FA7] mb-4" />
      <h3 className="font-display text-lg font-bold tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-[#495057] leading-relaxed">{desc}</p>
    </div>
  );
}

// v2.0.11 — Nouvelles cartes features dynamiques (i18n)
function FeatureI18n() {
  const { t } = useTranslation();
  return (
    <div className="p-6 bg-white border border-[#DEE2E6] elev-hover" data-testid="feature-i18n">
      <Globe className="h-6 w-6 text-[#059669] mb-4" />
      <h3 className="font-display text-lg font-bold tracking-tight mb-2">{t("landing.auto_lang")}</h3>
      <p className="text-sm text-[#495057] leading-relaxed">{t("landing.auto_lang_desc")}</p>
      <div className="mt-3 flex gap-2 text-lg" aria-hidden="true">🇫🇷 🇬🇧 🇩🇪 🇸🇪</div>
    </div>
  );
}

function FeatureCurrency() {
  const { t } = useTranslation();
  return (
    <div className="p-6 bg-white border border-[#DEE2E6] elev-hover" data-testid="feature-currency">
      <Coins className="h-6 w-6 text-[#F59E0B] mb-4" />
      <h3 className="font-display text-lg font-bold tracking-tight mb-2">{t("landing.multi_currency")}</h3>
      <p className="text-sm text-[#495057] leading-relaxed">{t("landing.multi_currency_desc")}</p>
      <div className="mt-3 flex gap-3 text-xs font-mono font-bold text-[#0A0A0A]" aria-hidden="true">
        <span>€</span><span>$</span><span>£</span><span>kr</span>
      </div>
    </div>
  );
}

function TarifItem({ text }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 className="h-4 w-4 text-[#FFD700] mt-0.5 flex-shrink-0" />
      <span className="opacity-90">{text}</span>
    </div>
  );
}

function FormationItem({ num, icon: Icon, title, desc }) {
  return (
    <div className="p-5 flex items-start gap-4">
      <div className="font-mono text-xs text-[#FFD700] font-bold mt-1">{num}</div>
      <Icon className="h-5 w-5 text-[#FFD700] mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-display font-bold tracking-tight">{title}</div>
        <div className="text-xs text-white/60 mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
